import { RuntimeActionType } from './toolViewerRuntimeAlerts';

const RUNTIME_DATA_META_KEYS = new Set([
  'action',
  'runtime_action',
  'request_id',
  'requestId',
  'entity_type',
  'entityType',
  'record_id',
  'recordId',
  'external_id',
  'externalId',
  'include_inactive',
  'page',
  'page_size',
  'is_active',
  'isActive',
  'data',
]);

const pickString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    const normalized = String(value || '').trim();
    if (normalized) {
      return normalized;
    }
  }
  return undefined;
};

const pickNestedString = (
  source: Record<string, unknown>,
  ...paths: string[]
): string | undefined => {
  for (const path of paths) {
    const segments = path.split('.');
    let current: unknown = source;
    let validPath = true;
    for (const segment of segments) {
      if (!current || typeof current !== 'object') {
        validPath = false;
        break;
      }
      current = (current as Record<string, unknown>)[segment];
    }
    if (!validPath) {
      continue;
    }
    const normalized = String(current || '').trim();
    if (normalized) {
      return normalized;
    }
  }
  return undefined;
};

const asObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const omitRuntimeMetaFields = (
  source: Record<string, unknown>
): Record<string, unknown> => {
  const cleaned: Record<string, unknown> = {};
  Object.entries(source).forEach(([key, value]) => {
    if (!RUNTIME_DATA_META_KEYS.has(key)) {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const hasOwnKeys = (value: Record<string, unknown>): boolean =>
  Object.keys(value).length > 0;

export const normalizeRuntimeActionRequest = (input: {
  action: RuntimeActionType;
  entityType?: string;
  recordId?: string;
  externalId?: string;
  data?: Record<string, unknown>;
  runtimeEntityNames?: string[];
}) => {
  const data =
    input.data && typeof input.data === 'object' ? input.data : {};
  const runtimeEntityName = (input.runtimeEntityNames || [])[0];

  const entityType = pickString(
    input.entityType,
    data.entity_type,
    data.entityType,
    pickNestedString(data, 'entity.type', 'dataset.entityType'),
    runtimeEntityName
  );
  const externalId = pickString(
    input.externalId,
    data.external_id,
    data.externalId,
    pickNestedString(
      data,
      'dataset.externalId',
      'data.external_id',
      'data.externalId',
      'payload.external_id',
      'payload.externalId'
    )
  );
  const recordId = pickString(
    input.recordId,
    externalId,
    data.record_id,
    data.recordId,
    data.id,
    data.external_id,
    data.externalId,
    pickNestedString(
      data,
      'record.id',
      'dataset.recordId',
      'data.id',
      'data.record_id',
      'data.recordId',
      'data.external_id',
      'data.externalId',
      'payload.id',
      'payload.record_id',
      'payload.recordId',
      'payload.external_id',
      'payload.externalId'
    )
  );

  return {
    action: input.action,
    entityType,
    recordId,
    externalId,
    data,
  };
};

export const resolveRuntimeActionData = (input: {
  rawData?: Record<string, unknown>;
  normalizedData?: Record<string, unknown>;
}): Record<string, unknown> => {
  const rawData = asObject(input.rawData);
  const normalizedData = asObject(input.normalizedData);
  const topLevelData = omitRuntimeMetaFields(rawData);
  if (hasOwnKeys(topLevelData)) {
    return topLevelData;
  }

  const nestedData = asObject(rawData.data);
  if (hasOwnKeys(nestedData)) {
    return nestedData;
  }

  return normalizedData;
};
